// pages/LinkedAccounts/Linked-Accounts.js
import React, { useState, useEffect, useCallback, useMemo } from "react"; // Added useMemo
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

import MainLinkedAccounts from './MainLinkedAccounts';
import OthersLinkedAccounts from './OthersLinkedAccounts';

const tabSlugs = [
    'linked-accounts', 
    'my-linked-accounts'
];

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            style={{ width: '100%' }}
            {...other}
        >
            {value === index && <Box sx={{ p: { xs: 1, md: 0} }}>{children}</Box>}
        </div>
    );
}
TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `vertical-tab-${index}`,
        "aria-controls": `vertical-tabpanel-${index}`,
    };
}

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function LinkedAccounts() {
    useEffect(() => {
        document.title = 'Linked Accounts | KlonIT Web Application';
    }, []);

    const [searchParams, setSearchParams] = useSearchParams();

    // activeTabIndex is derived from the URL search parameters.
    // URL is the single source of truth.
    const activeTabIndex = useMemo(() => {
        const tabQuery = searchParams.get('tab')?.toLowerCase();
        const index = tabSlugs.indexOf(tabQuery);
        // Default to the first tab in tabSlugs if not found or invalid
        return index !== -1 ? index : tabSlugs.indexOf(tabSlugs[0]);
    }, [searchParams]); // tabSlugs is constant, not needed in deps array explicitly

    // This useEffect ensures that the URL always reflects a valid tab slug,
    // especially on initial load if the 'tab' param is missing or invalid.
    useEffect(() => {
        const currentSlugInUrl = searchParams.get('tab')?.toLowerCase();
        const expectedSlug = tabSlugs[activeTabIndex];

        // If the derived activeTabIndex implies a slug that's not in the URL, update the URL.
        // This typically happens on initial load if `?tab=` is missing, causing activeTabIndex to default.
        if (expectedSlug && currentSlugInUrl !== expectedSlug) {
            setSearchParams({ tab: expectedSlug }, { replace: true });
        }
    }, [activeTabIndex, searchParams, setSearchParams, tabSlugs]); // tabSlugs is constant

    const handleChange = (event, newValue) => {
        // When a tab is clicked, update the URL.
        // The activeTabIndex (derived via useMemo) will update automatically due to searchParams changing.
        const newSlug = tabSlugs[newValue];
        if (searchParams.get('tab')?.toLowerCase() !== newSlug) {
            setSearchParams({ tab: newSlug }, { replace: true });
        }
    };

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    const showMessage = useCallback((message, severity = "success") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    }, []);

    return (
        <Box sx={{ display: 'flex', flexGrow: 1, mt: { xs: 1, md: 3 } }} >
            <Box component="main" sx={{ flexGrow: 1, p: { xs: 1, sm: 2, md: 3 } }}>
                <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
                   Linked Accounts Management
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <Box sx={{
                        borderColor: 'divider',
                        mb: { xs: 2, md: 0 },
                        mr: { xs: 0, md: 3 },
                        minWidth: { xs: '100%', md: '240px' },
                        borderRight: { xs: 0, md: 1 },
                        borderBottom: { xs: 1, md: 0 },
                        borderColor: "divider",
                    }}>
                        <Tabs
                            orientation={"vertical"}
                            variant="scrollable"
                            value={activeTabIndex}
                            onChange={handleChange}
                            aria-label="Linked accounts tabs"
                            sx={{
                                display: { xs: 'flex', md: 'block'},
                                flexDirection: { xs: 'row', md: 'column' },
                                '& .MuiTabs-indicator': {
                                    left: { xs: 0, md: 'auto' },
                                    right: { xs: 'auto', md: 0 },
                                    width: {xs: 'auto', md: '3px'},
                                    bottom: {xs: 0, md: 'auto'},
                                },
                                '& .MuiTab-root': {
                                    alignItems: { xs: 'center', md: 'flex-start'},
                                    textAlign: {xs: 'center', md: 'left'},
                                    minWidth: { xs: 'auto', md: '220px' },
                                    flexGrow: { xs: 1, md: 0 },
                                    px: {xs: 1, md: 2},
                                    py: {xs: 1.5, md: 2},
                                    fontSize: {xs: '0.8rem', sm: '0.875rem'}
                                }
                             }}
                         >
                            {/* Tab order corresponds to tabSlugs array indexes */}
                            <Tab label="Linked Accounts" {...a11yProps(0)} /> {/* Corresponds to tabSlugs[0] */}
                            <Tab label="My Linked Account" {...a11yProps(1)} />      {/* Corresponds to tabSlugs[1] */}
                        </Tabs>
                    </Box>

                    <Box sx={{ flexGrow: 1, width: '100%', p: {xs: 0, md: 2} }}>
                        <TabPanel value={activeTabIndex} index={0}>
                            <OthersLinkedAccounts showMessage={showMessage} />
                        </TabPanel>
                        <TabPanel value={activeTabIndex} index={1}>
                            <MainLinkedAccounts showMessage={showMessage} />
                        </TabPanel>
                    </Box>
                </Box>
            </Box>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}